import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Edit, 
  Save, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Printer, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Settings
} from 'lucide-react';
import classSubjectQuotaService from '../../services/classSubjectQuotaService';
import '../../styles/ClassSubjectItemQuota.css';

const ClassSubjectItemQuota = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [quotaGrid, setQuotaGrid] = useState({ subjects: [], classes: [], grid: {}, rawData: [] });
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [selectedQuota, setSelectedQuota] = useState(null);
  const [newSessions, setNewSessions] = useState('');
  const [actionType, setActionType] = useState('');

  // Load quota data for current page
  useEffect(() => {
    loadQuotaData();
  }, [currentPage]);

  const loadQuotaData = async () => {
    setLoading(true);
    try {
      const data = await classSubjectQuotaService.getQuotaGrid(currentPage);
      setQuotaGrid(data);
    } catch (error) {
      console.error('Error loading quota data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle cell double-click for editing
  const handleCellDoubleClick = (subjectCode, classCode) => {
    const cellData = quotaGrid.grid[subjectCode]?.[classCode];
    if (cellData) {
      setSelectedCell({ subjectCode, classCode });
      setEditingValue(cellData.quota_value || '');
      setIsEditing(true);
    }
  };

  // Handle cell click for selection
  const handleCellClick = (subjectCode, classCode) => {
    const cellData = quotaGrid.grid[subjectCode]?.[classCode];
    if (cellData) {
      setSelectedQuota(cellData);
      setShowActions(true);
    } else {
      setSelectedQuota(null);
      setShowActions(false);
    }
  };

  // Save edited value
  const handleSaveEdit = async () => {
    if (!selectedCell || !editingValue.trim()) return;

    try {
      await classSubjectQuotaService.updateQuotaValue(
        selectedCell.subjectCode,
        selectedCell.classCode,
        editingValue.trim(),
        currentPage
      );
      
      // Reload data to reflect changes
      await loadQuotaData();
      setIsEditing(false);
      setSelectedCell(null);
      setEditingValue('');
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('儲存失敗，請重試 (Save failed, please try again)');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedCell(null);
    setEditingValue('');
  };

  // Handle action buttons
  const handleMakeSplit = () => {
    if (!selectedQuota) return;
    setActionType('split');
    setNewSessions('');
  };

  const handleMakeCombined = () => {
    if (!selectedQuota) return;
    setActionType('combined');
    setNewSessions('');
  };

  const handleMakeAtomic = () => {
    if (!selectedQuota) return;
    setActionType('atomic');
    setNewSessions('');
  };

  const handleModifyQuotaSize = () => {
    if (!selectedQuota) return;
    setActionType('modify');
    setNewSessions(selectedQuota.quota_sessions?.toString() || '');
  };

  const handleDeleteChunk = async () => {
    if (!selectedQuota) return;
    
    const confirmed = window.confirm('確定要刪除此配額項目嗎？ (Are you sure you want to delete this quota item?)');
    if (confirmed) {
      try {
        await classSubjectQuotaService.deleteChunk(selectedQuota.id);
        await loadQuotaData();
        setSelectedQuota(null);
        setShowActions(false);
        alert('配額項目已刪除 (Quota item deleted)');
      } catch (error) {
        console.error('Error deleting chunk:', error);
        alert('刪除失敗，請重試 (Delete failed, please try again)');
      }
    }
  };

  // Execute action
  const handleExecuteAction = async () => {
    if (!selectedQuota || !newSessions || isNaN(newSessions) || newSessions < 0) {
      alert('請輸入有效的課節數 (Please enter a valid number of sessions)');
      return;
    }

    try {
      switch (actionType) {
        case 'split':
          await classSubjectQuotaService.makeSplit(
            selectedQuota.subject_code,
            selectedQuota.class_code,
            parseInt(newSessions),
            currentPage
          );
          break;
        case 'combined':
          await classSubjectQuotaService.makeCombined(
            selectedQuota.subject_code,
            selectedQuota.class_code,
            parseInt(newSessions),
            currentPage
          );
          break;
        case 'atomic':
          await classSubjectQuotaService.makeAtomic(
            selectedQuota.subject_code,
            selectedQuota.class_code,
            parseInt(newSessions),
            currentPage
          );
          break;
        case 'modify':
          await classSubjectQuotaService.modifyQuotaSize(
            selectedQuota.id,
            parseInt(newSessions)
          );
          break;
        default:
          return;
      }

      await loadQuotaData();
      setActionType('');
      setNewSessions('');
      setSelectedQuota(null);
      setShowActions(false);
      alert('操作完成 (Operation completed)');
    } catch (error) {
      console.error('Error executing action:', error);
      alert('操作失敗，請重試 (Operation failed, please try again)');
    }
  };

  // Get cell styling based on quota type
  const getCellStyle = (cellData) => {
    if (!cellData) return 'cell-normal';
    
    if (cellData.is_combined) return 'cell-combined';
    if (cellData.is_split) return 'cell-split';
    if (cellData.is_atomic) return 'cell-atomic';
    if (cellData.quota_sessions < 0) return 'cell-negative';
    
    return 'cell-normal';
  };

  // Get cell text color based on quota type
  const getCellTextColor = (cellData) => {
    if (!cellData) return 'text-gray-500';
    
    if (cellData.is_combined) return 'text-blue-800';
    if (cellData.is_split) return 'text-red-800';
    if (cellData.is_atomic) return 'text-green-800';
    if (cellData.quota_sessions < 0) return 'text-red-900 font-bold';
    
    return 'text-gray-900';
  };

  if (loading && quotaGrid.subjects.length === 0) {
    return (
      <div className="class-subject-quota-container">
        <div className="loading-container">
          <div className="loading-content">
            <RefreshCw className="loading-spinner" size={48} />
            <p className="loading-text">載入配額資料中... (Loading quota data...)</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-subject-quota-container">
      {/* Header */}
      <div className="class-subject-quota-header">
        <h2>班別科目配額分配編修 (Class-Subject Item Quota Division)</h2>
        <p className="subtitle">TBM0170E</p>
        <div className="page-indicator">Page: {currentPage + 1}</div>
      </div>

      {/* Navigation Controls */}
      <div className="navigation-controls">
        <div className="navigation-buttons">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="nav-button secondary"
          >
            <ChevronLeft size={16} />
            Prev Page
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= 9}
            className="nav-button secondary"
          >
            Next Page
            <ChevronRight size={16} />
          </button>
        </div>
        
        <div className="action-buttons">
          <button className="nav-button primary">
            <Printer size={16} />
            Print Page
          </button>
          <button className="nav-button secondary">
            Close
          </button>
        </div>
      </div>

      {/* Quota Grid */}
      <div className="quota-grid-container">
        <table className="quota-grid">
          <thead>
            <tr>
              <th>Subject/Item</th>
              {quotaGrid.classes.map(classCode => (
                <th key={classCode}>
                  {classCode}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {quotaGrid.subjects.map(subjectCode => (
              <tr key={subjectCode}>
                <td>{subjectCode}</td>
                {quotaGrid.classes.map(classCode => {
                  const cellData = quotaGrid.grid[subjectCode]?.[classCode];
                  const isSelected = selectedCell?.subjectCode === subjectCode && selectedCell?.classCode === classCode;
                  const isSelectedForAction = selectedQuota?.subject_code === subjectCode && selectedQuota?.class_code === classCode;
                  
                  return (
                    <td key={`${subjectCode}-${classCode}`}>
                      <div
                        className={`quota-cell ${getCellStyle(cellData)} ${
                          isSelectedForAction ? 'selected' : ''
                        } ${isSelected && isEditing ? 'editing' : ''}`}
                        onClick={() => handleCellClick(subjectCode, classCode)}
                        onDoubleClick={() => handleCellDoubleClick(subjectCode, classCode)}
                      >
                        {isSelected && isEditing ? (
                          <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            onBlur={handleSaveEdit}
                            className="quota-input"
                            autoFocus
                          />
                        ) : (
                          <span className="quota-value">
                            {cellData?.quota_value || '-'}
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Panel */}
      {showActions && selectedQuota && (
        <div className="action-panel">
          <div className="action-panel-header">
            <h4 className="action-panel-title">Class-SubItem</h4>
          </div>
          
          <div className="selected-info">
            <p className="selected-info-text">
              已選擇: {selectedQuota.subject_code} - {selectedQuota.class_code}
            </p>
            <p className="selected-info-text">
              當前配額: {selectedQuota.quota_value || '-'}
            </p>
          </div>
          
          <div className="action-buttons-grid">
            <button
              onClick={handleMakeSplit}
              className="action-button split"
            >
              <Grid size={16} />
              Make SPLIT
            </button>
            <button
              onClick={handleMakeCombined}
              className="action-button combined"
            >
              <CheckCircle size={16} />
              Make COMBINED
            </button>
            <button
              onClick={handleMakeAtomic}
              className="action-button atomic"
            >
              <Settings size={16} />
              Make ATOMIC
            </button>
            <button
              onClick={handleModifyQuotaSize}
              className="action-button modify"
            >
              <Edit size={16} />
              Modify Quota Size
            </button>
            <button
              onClick={handleDeleteChunk}
              className="action-button delete"
            >
              <Trash2 size={16} />
              Delete CHUNK
            </button>
          </div>
        </div>
      )}

      {/* Action Input Dialog */}
      {actionType && (
        <div className="action-dialog">
          <div className="action-dialog-content">
            <h3 className="action-dialog-title">
              {actionType === 'split' && '分班〔一班分拆為兩班〕(Make Split)'}
              {actionType === 'combined' && '合班〔一班分拆為兩班〕(Make Combined)'}
              {actionType === 'atomic' && '分班〔一班分由兩個教師授課〕(Make Atomic)'}
              {actionType === 'modify' && '修改配額 (Modify Quota Size)'}
            </h3>
            <input
              type="number"
              value={newSessions}
              onChange={(e) => setNewSessions(e.target.value)}
              className="action-dialog-input"
              placeholder="課節數 (Number of Sessions)"
              min="0"
              autoFocus
            />
            <div className="action-dialog-buttons">
              <button
                onClick={() => {
                  setActionType('');
                  setNewSessions('');
                }}
                className="action-dialog-button cancel"
              >
                取消 (Cancel)
              </button>
              <button
                onClick={handleExecuteAction}
                className="action-dialog-button confirm"
              >
                確定 (OK)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions-section">
        <h5 className="instructions-title">使用說明 (Instructions):</h5>
        <ol className="instructions-list">
          <li>點擊「Next Page」按鈕進入頁面0，顯示課節結餘數值</li>
          <li>點擊「Next Page」按鈕進入首頁，開始九頁編修畫面</li>
          <li>可進行三種設定：分班〔一班分由兩個教師授課〕(Make Atomic)、分班〔一班分拆為兩班〕(Make Split)、合班〔一班分拆為兩班〕(Make Combined)</li>
          <li>雙擊儲存格可編輯配額值</li>
          <li>選擇儲存格後可使用動作按鈕進行操作</li>
          <li>紅色文字表示分班，藍色文字表示合班</li>
          <li>負數表示配額分配有問題，需要修正</li>
          <li>完成後可回到主頁檢查分班/合班情況</li>
        </ol>
      </div>
    </div>
  );
};

export default ClassSubjectItemQuota;
